import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';
import { shortHash } from '../engine/hash';

const SVG_SOURCE = 'https://www.w3.org/TR/SVG2/';
const TEXTMETRICS_SOURCE = 'https://www.w3.org/TR/css-fonts-4/#textmetrics';

/**
 * SVG text metrics, DOMRect geometry, and emoji rendering.
 *
 * Three related but distinct surfaces that CreepJS treats separately:
 *
 *  - SVG text metrics (getComputedTextLength / getExtentOfChar) go through a
 *    different shaping and layout path than canvas fillText, so they are a
 *    genuinely independent fingerprint of the font stack + engine.
 *  - DOMRect geometry of a laid-out element exercises the layout engine's
 *    sub-pixel rounding, which differs between Blink/Gecko/WebKit and between
 *    device scale factors.
 *  - Emoji rendering goes through the platform emoji font, which is one of the
 *    highest-entropy, hardest-to-spoof signals on the platform (Segoe UI Emoji
 *    on Windows, Apple Color Emoji on macOS/iOS, Noto Color Emoji on Linux).
 *
 * Credit: surface selection and method from CreepJS (MIT) src/svg, src/domrect,
 * src/samples. Implemented here independently.
 */

const EMOJI = [
  { cp: 0x1f600, name: 'grinning' },
  { cp: 0x1f680, name: 'rocket' },
  { cp: 0x1f41b, name: 'bug' },
  { cp: 0x1f4a1, name: 'bulb' },
  { cp: 0x1f469, name: 'woman' },
  { cp: 0x1f9d1, name: 'adult' },
  { cp: 0x2764, name: 'heart' },
  { cp: 0x1f44d, name: 'thumbsup' },
];

export const svgRectProbe: Probe = {
  id: 'svgRect',
  label: 'SVG / DomRect / emoji',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: 'SVG text metrics, sub-pixel rect geometry, and emoji font rendering',
  sources: [SVG_SOURCE, TEXTMETRICS_SOURCE],
  budgetMs: 2500,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const host = document.createElement('div');
    host.setAttribute(
      'style',
      'position:absolute;left:-10000px;top:0;visibility:hidden;width:400px;',
    );
    document.body.appendChild(host);

    // --- 1. SVG text metrics ----------------------------------------------
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', '300');
    svg.setAttribute('height', '100');
    host.appendChild(svg);
    const text = document.createElementNS(NS, 'text');
    text.setAttribute('x', '10');
    text.setAttribute('y', '50');
    text.setAttribute('font-size', '24px');
    text.textContent = 'Cwm fjordbank glyphs vext quiz';
    svg.appendChild(text);

    let svgMetrics: Record<string, number | null> = {};
    let svgMetricsError: string | null = null;
    let svgHash = '';
    try {
      const len = text.getComputedTextLength?.() ?? null;
      const ext = text.getExtentOfChar?.(0) ?? null;
      const sub = text.getSubStringLength?.(0, 5) ?? null;
      svgMetrics = {
        computedTextLength: len == null ? null : sig(len, 3),
        extentWidth: ext ? sig(ext.width, 3) : null,
        extentHeight: ext ? sig(ext.height, 3) : null,
        subStringLength: sub == null ? null : sig(sub, 3),
      };
      svgHash = await shortHash(JSON.stringify(svgMetrics));
    } catch (e) {
      svgMetricsError = String((e as Error)?.message ?? e);
    }

    // --- 2. DOMRect sub-pixel geometry -------------------------------------
    // Lay out an element at a fractional offset and read the rect. The
    // rounding of that rect is layout-engine specific.
    const box = document.createElement('div');
    box.setAttribute(
      'style',
      'position:absolute;left:10.5px;top:20.25px;width:33.75px;height:17.5px;',
    );
    host.appendChild(box);
    const rect = box.getBoundingClientRect();
    const rectMetrics = {
      left: sig(rect.left, 3),
      top: sig(rect.top, 3),
      width: sig(rect.width, 3),
      height: sig(rect.height, 3),
      // x/y are the pre-rounding coordinates; a mismatch between x and left
      // reveals the engine's rounding behaviour.
      x: sig(rect.x, 3),
      y: sig(rect.y, 3),
    };
    const rectHash = await shortHash(JSON.stringify(rectMetrics));

    // --- 3. Emoji rendering ------------------------------------------------
    // Render each emoji in a generic font stack; the platform emoji font still
    // resolves and the resulting pixels/widths identify it.
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const g = canvas.getContext('2d', { willReadFrequently: true });
    const emojiMetrics: Record<string, { width: number | null; hash: string | null }> = {};
    if (g) {
      g.font = '20px monospace';
      for (const e of EMOJI) {
        const ch = String.fromCodePoint(e.cp);
        const w = sig(g.measureText(ch).width, 3);
        let hash: string | null = null;
        try {
          g.clearRect(0, 0, 32, 32);
          g.fillText(ch, 0, 20);
          const data = g.getImageData(0, 0, 32, 32).data;
          // Hash the pixel content: this is what distinguishes the actual
          // colour emoji font (Segoe / Apple / Noto) from each other.
          hash = await shortHash(
            Array.from(data)
              .filter((_, i) => i % 4 !== 3) // drop alpha
              .join(','),
          );
        } catch {
          hash = null; // tainted canvas
        }
        emojiMetrics[e.name] = { width: w, hash };
      }
    }
    const emojiHash = await shortHash(JSON.stringify(emojiMetrics));

    host.remove();

    // A missing SVG text-metrics API on a modern browser is a stripped-renderer
    // signal (headless Chrome without full layout).
    const svgMissing =
      svgMetrics.computedTextLength === null && svgMetricsError === null;
    if (svgMissing) {
      findings.push({
        id: 'svgrect.no-text-metrics',
        severity: 'low',
        category: 'hardware',
        layer: 'gpu',
        confidence: 'low',
        title: 'SVG text metrics are unavailable',
        detail:
          'getComputedTextLength returned nothing. Most modern browsers implement it; its absence can indicate a stripped-down renderer used by some automation stacks.',
        sources: [SVG_SOURCE],
        evidence: ['getComputedTextLength() produced no value'],
        scoreContribution: 0, // weak on its own, environment-dependent
      });
    }

    return {
      signals: {
        available: ok(true, 'client'),
        svgMetrics: ok(svgMetrics, 'client'),
        svgMetricsError: ok(svgMetricsError, 'client'),
        svgHash: ok(svgHash, 'derived'),
        rectMetrics: ok(rectMetrics, 'client'),
        rectHash: ok(rectHash, 'derived'),
        emojiMetrics: ok(emojiMetrics, 'client'),
        emojiHash: ok(emojiHash, 'derived'),
        svgTextMetricsMissing: ok(svgMissing, 'derived'),
      },
      findings,
      parts: {
        gpu: {
          // All three are stable, engine+platform-specific hashes.
          svgHash,
          rectHash,
          emojiHash,
        },
      },
    };
  },
};

registerProbe(svgRectProbe);
